import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import {
  getWarholOutputSettings,
  updateWarholOutputSettings,
} from "../../services/warhol-output-settings";

export const warholOutputSettingsKeys = {
  all: ["warhol-output-settings"] as const,
};

export function useWarholOutputSettingsQuery() {
  return useQuery({
    queryKey: warholOutputSettingsKeys.all,
    queryFn: getWarholOutputSettings,
  });
}

export function useUpdateWarholOutputSettingsMutation() {
  const queryClient = useQueryClient();
  const latestRequestRef = useRef(0);
  return useMutation({
    mutationFn: updateWarholOutputSettings,
    onMutate: () => {
      latestRequestRef.current += 1;
      return { requestId: latestRequestRef.current };
    },
    onSuccess: async (data, _variables, context) => {
      // why: overlapping PUTs finish out of order — only the latest request may replace the cached draft.
      if (context?.requestId !== latestRequestRef.current) {
        return;
      }
      // why: a GET that started before this PUT landed resolves afterwards and
      // overwrites the cache with pre-save text (settings page remounts refetch
      // on mount), so the user watches their saved guidance revert. Cancel the
      // in-flight read before the authoritative write — cancelling after would
      // revert the query to the state captured when that read began.
      await queryClient.cancelQueries({
        queryKey: warholOutputSettingsKeys.all,
        exact: true,
      });
      // why: a newer PUT can start while we are parked on the cancel above. It
      // may already have written the cache, so re-check the generation rather
      // than clobbering the newer draft with this older response.
      if (context.requestId !== latestRequestRef.current) {
        return;
      }
      queryClient.setQueryData(warholOutputSettingsKeys.all, data);
    },
  });
}
